/**
 * Rule Delete Policy (TV1-1696)
 *
 * Deletes a single rule from the Reglas catalog via `backend.commands.RemoveRule`
 * (`{ id: entityId, body: { ruleId }}`, matching the call shape used across the
 * app — account / channel / item rule managers).
 *
 * Trigger: the CDSE engine reacts to STATE changes. The user confirms the delete
 * modal, which dispatches the user-sourced `ui/requestRuleDelete` (allowlisted in
 * canApply). That sets `ui.pendingDelete` non-null; this policy reacts to the
 * `null → non-null` transition, deletes, refetches the account rule set
 * (read-after-write), and clears `pendingDelete` in `finally`.
 *
 * Errors are re-thrown to the global handler — no manual notifications.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { RulesReadmodelType } from '@bett3r-dev/teselly-domain';
import { RulesCatalogState } from '../state';
import { RulesCatalogCommand } from '../commands';

type Dispatch = ( command: RulesCatalogCommand, meta: { source: string }) => void;

/** Same fetch shape as the toggle / scope-reference policies (incl. bypass rows). */
const RULES_FILTER: NonNullable<
  Parameters<ClientLibraryType['queries']['Rules']>[0]
>['filter'] = [
  {
    OR: [
      { field: 'kind', op: 'eq', value: 'bypass' },
      { field: 'kind', op: 'notExists' }
    ]
  }
];

const RULES_SELECT: ( keyof RulesReadmodelType )[] = [
  'id',
  'name',
  'description',
  'ruleDefinition',
  'ruleDefinitionId',
  'ruleId',
  'kind',
  'targets',
  'enabled',
  'categories',
  'triggeringEvents',
  'declaredInputsValues',
  'hierarchy',
  'hierarchyLevel',
  'hierarchyEntityIds',
  'overrideGroups'
];

export const createRuleDeletePolicy = (
  backend: ClientLibraryType
): Policy<RulesCatalogState, RulesCatalogCommand> => {
  let closed = false;

  const refetchRules = async ( dispatch: Dispatch ) => {
    const rules = await backend.queries.Rules({
      filter: RULES_FILTER,
      select: RULES_SELECT
    });
    if ( closed ) return;
    dispatch(
      { type: 'automation/rulesCatalog/data/setRules', payload: { rules: rules || []}},
      { source: 'api' }
    );
  };

  const applyDelete = async ( rule: RulesReadmodelType, dispatch: Dispatch ) => {
    // RemoveRule's `id` is the aggregate root key — the rule's first hierarchy
    // entity id (falling back to the rule id); the body carries the ruleId.
    const hierarchyEntityIds = rule.hierarchyEntityIds as string[] | undefined;
    const entityId = hierarchyEntityIds?.[0] || rule.id;

    await backend.commands.RemoveRule({
      id: entityId as string,
      body: { ruleId: rule.ruleId }
    } as Parameters<ClientLibraryType['commands']['RemoveRule']>[0]);

    await refetchRules( dispatch );
  };

  return {
    name: 'automation-rules-catalog-delete',

    shouldReact: ( prev, next ) =>
      prev.ui.pendingDelete === null && next.ui.pendingDelete !== null,

    onStateChange: async ({ next, ctx }) => {
      const pending = next.ui.pendingDelete;
      if ( !pending ) return;

      try {
        const rule = next.data.rules.find(( r ) => r.id === pending.ruleId );
        if ( rule ) {
          await applyDelete( rule, ctx.dispatch );
        }
      } finally {
        if ( !closed ) {
          ctx.dispatch(
            {
              type: 'automation/rulesCatalog/ui/clearPendingDelete',
              payload: {} as Record<string, never>
            },
            { source: 'api' }
          );
        }
      }
    },

    cleanup: () => {
      closed = true;
    }
  };
};
